import { apiFetch } from '../utils/apiClient';

export type PublicAuthIntent = 'SIGNUP' | 'LOGIN' | 'PASSWORD_SETUP';

export interface PublicAuthRequestCodePayload {
    email: string;
    intent: PublicAuthIntent;
}

export interface PublicAuthVerifyPayload {
    email: string;
    code: string;
    intent: PublicAuthIntent;
    fullName?: string;
    password?: string;
}

export interface PublicAuthPasswordLoginPayload {
    email: string;
    password: string;
}

export interface PublicAuthSession {
    userId: number;
    email: string;
    fullName?: string | null;
    tenantId: number;
    token: string;
    expiresIn: number;
    expiresAt: number;
    hasPassword: boolean;
    mustChangePassword: boolean;
}

export interface PublicAuthOtpRequestResponse {
    status: string;
    expiresAt: string | null;
    ttlSeconds: number | null;
}

const readErrorMessage = async (res: Response): Promise<string> => {
    try {
        const text = await res.text();
        if (!text) return `HTTP ${res.status}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === 'object') {
                if (typeof parsed.message === 'string') return parsed.message;
                if (typeof parsed.error === 'string') return parsed.error;
                if (typeof parsed.code === 'string') return parsed.code;
            }
        } catch {
            /* ignore parse errors */
        }
        return text;
    } catch {
        return `HTTP ${res.status}`;
    }
};

const computeExpiry = (expiresIn: unknown): { expiresIn: number; expiresAt: number } => {
    const fallback = 60 * 60 * 1000; // 1 ora
    const duration = typeof expiresIn === 'number' && Number.isFinite(expiresIn) ? expiresIn : fallback;
    const safeDuration = duration > 0 ? duration : fallback;
    const expiresAt = Date.now() + safeDuration;
    return { expiresIn: safeDuration, expiresAt };
};

const mapSession = (payload: any): PublicAuthSession => {
    const userId = typeof payload?.userId === 'number' ? payload.userId : Number(payload?.userId ?? 0);
    const tenantId = typeof payload?.tenantId === 'number' ? payload.tenantId : Number(payload?.tenantId ?? 0);
    const email = typeof payload?.email === 'string' ? payload.email : '';
    const fullName = typeof payload?.fullName === 'string' ? payload.fullName : null;
    const token = typeof payload?.token === 'string' ? payload.token : '';
    const hasPassword = Boolean(payload?.hasPassword);
    const mustChangePassword = Boolean(payload?.mustChangePassword);
    const { expiresIn, expiresAt } = computeExpiry(payload?.expiresIn);

    return {
        userId,
        tenantId,
        email,
        fullName,
        token,
        expiresIn,
        expiresAt,
        hasPassword,
        mustChangePassword,
    };
};

export const requestPublicAuthCode = async (
    tenantIdentifier: string,
    payload: PublicAuthRequestCodePayload,
    tenantId?: string | null
): Promise<PublicAuthOtpRequestResponse> => {
    const res = await apiFetch(`/api/public/tenants/${tenantIdentifier}/auth/code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId: tenantId ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return {
        status: typeof data?.status === 'string' ? data.status : 'OTP_SENT',
        expiresAt: typeof data?.expiresAt === 'string' ? data.expiresAt : null,
        ttlSeconds: typeof data?.ttlSeconds === 'number' ? data.ttlSeconds : null,
    };
};

export const verifyPublicAuthCode = async (
    tenantIdentifier: string,
    payload: PublicAuthVerifyPayload,
    tenantId?: string | null
): Promise<PublicAuthSession> => {
    const res = await apiFetch(`/api/public/tenants/${tenantIdentifier}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId: tenantId ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return mapSession(data);
};

export const loginWithPublicPassword = async (
    tenantIdentifier: string,
    payload: PublicAuthPasswordLoginPayload,
    tenantId?: string | null
): Promise<PublicAuthSession> => {
    const res = await apiFetch(`/api/public/tenants/${tenantIdentifier}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId: tenantId ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return mapSession(data);
};
